import { useCallback, useMemo, useState } from "react";
import { QueryClient, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  CONVERSATION_SEGMENT_GET,
  PARTICIPANTS_GET,
  sendLog,
  updateConversationSegment,
} from "@/api";
import {
  Client,
  LogLevel,
  TranscriptSegment,
  User,
  Participant,
} from "@/types";
import { ANALYTICS_EVENTS } from "@/constants";
import { showErrorToast } from "@/lib/errorUtils";
import { useAnalytics } from "@/lib";

interface SelectToReassign {
  segment: TranscriptSegment;
  participantId: string;
  speakerId: string;
  speakerToSwitchId?: string | "unknown";
  createData?: {
    data: Client | User;
    type: "client" | "user";
  };
}

interface Params {
  conversationId: string;
  queryClient: QueryClient;
  updateParticipantAsync: (
    data: Partial<Participant>,
  ) => Promise<Participant | undefined>;
  markSpeakerUpdated: () => void;
}

export const useConversationSegmentMutation = ({
  conversationId,
  queryClient,
  updateParticipantAsync,
  markSpeakerUpdated,
}: Params) => {
  const { track } = useAnalytics();
  const [selectReassign, setSelectReassign] = useState<SelectToReassign | null>(
    null,
  );

  const {
    mutate: updateSegment,
    isPending: isUpdatePending,
    variables,
  } = useMutation({
    mutationFn: ({
      segmentId,
      newSpeakerId,
    }: {
      segmentId: string;
      newSpeakerId: string;
    }) =>
      updateConversationSegment(conversationId, segmentId, {
        speakerId: newSpeakerId,
      }),
    onMutate: ({ segmentId }) => {
      const segments = queryClient.getQueryData<TranscriptSegment[]>([
        CONVERSATION_SEGMENT_GET,
        conversationId,
      ]);
      const originalSegment = segments?.find((s) => s.id === segmentId);
      return { originalSpeakerId: originalSegment?.speakerId ?? null };
    },
    onSuccess: (_data, { newSpeakerId }, context) => {
      queryClient.setQueryData(
        [CONVERSATION_SEGMENT_GET, conversationId],
        (oldData: TranscriptSegment[]) => {
          if (!oldData || !Array.isArray(oldData)) return oldData;
          return oldData.map((segment) => {
            if (segment.id === variables?.segmentId) {
              return { ...segment, speakerId: variables?.newSpeakerId };
            }
            return segment;
          });
        },
      );
      queryClient.invalidateQueries({
        queryKey: [CONVERSATION_SEGMENT_GET, conversationId],
      });
      toast.success("Speaker updated successfully");
      markSpeakerUpdated();

      track(ANALYTICS_EVENTS.SEGMENT_SPEAKER_CHANGED, {
        conversationId,
        segmentId: variables?.segmentId,
        oldSpeakerId: context?.originalSpeakerId ?? null,
        newSpeakerId,
      });

      // Clean up orphaned participant-speaker links.
      const oldSpeakerId = context?.originalSpeakerId;
      if (oldSpeakerId && oldSpeakerId !== newSpeakerId) {
        const updatedSegments = queryClient.getQueryData<TranscriptSegment[]>([
          CONVERSATION_SEGMENT_GET,
          conversationId,
        ]);
        if (updatedSegments) {
          const oldSpeakerHasSegments = updatedSegments.some(
            (s) => s.speakerId === oldSpeakerId,
          );
          if (!oldSpeakerHasSegments) {
            const currentParticipants = queryClient.getQueryData<Participant[]>([
              PARTICIPANTS_GET,
              conversationId,
            ]);
            const orphanedParticipants = currentParticipants?.filter(
              (p) => p.speakerId === oldSpeakerId,
            );
            if (orphanedParticipants?.length) {
              Promise.all(
                orphanedParticipants.map((p) =>
                  updateParticipantAsync({
                    id: p.id,
                    conversationId,
                    speakerId: null,
                  }),
                ),
              ).catch(() => {
                // Error toast handled by mutation's onError
              });
            }
          }
        }
      }
    },
    onError: (error, vars) => {
      showErrorToast(error, "conversation");
      sendLog({
        level: LogLevel.ERROR,
        message: "Failed to update speaker",
        component: "ConversationDetailsProvider",
        data: {
          ...vars,
          conversationId,
          error: (error as Error)?.message || "",
        },
      });
    },
  });

  const segmentUpdating = useMemo(() => {
    return isUpdatePending ? variables?.segmentId : undefined;
  }, [isUpdatePending, variables]);

  return {
    updateSegment,
    segmentUpdating,
    selectReassign,
    setSelectReassign: useCallback(
      (value: SelectToReassign | null) => setSelectReassign(value),
      [],
    ),
  };
};
